import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView } from '../lib/analytics';

// 市場熱話單則詳情（站內分頁；2026-10-19 Kevin 需求：日期＋標題＋詳細內容＋資料來源＋返回，唔離開網站）
interface NewsItem {
  id: number;
  news_date: string;
  title: string;
  summary: string;
  content?: string;
  source_name: string;
  source_url: string;
  image_url?: string;
}

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // 上一則／下一則（列表序：新→舊；上一則=較新一篇，下一則=較舊一篇）
  const [prev, setPrev] = useState<NewsItem | null>(null);
  const [next, setNext] = useState<NewsItem | null>(null);

  useEffect(() => {
    trackPageView(`/media-coverage/${id ?? ''}`);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setItem(null);
    fetch(`/api/public/media-news/${encodeURIComponent(id ?? '')}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => {
        if (cancelled) return;
        if (j.success && j.data) setItem(j.data);
        else setNotFound(true);
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/public/media-news')
      .then((r) => r.json())
      .then((j) => {
        if (cancelled || !j.success || !Array.isArray(j.data) || !id) return;
        const list = j.data as NewsItem[];
        const idx = list.findIndex((n) => String(n.id) === String(id));
        if (idx === -1) return;
        setPrev(idx > 0 ? list[idx - 1] : null);
        setNext(idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  const body = (item?.content && item.content.trim()) || item?.summary?.trim() || '';

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="max-w-3xl mx-auto px-4 pt-6 pb-2">
        <Link
          to="/media-coverage"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-stone-500 hover:text-x1493-primary transition"
        >
          <ArrowLeft size={16} /> 返回媒體報導
        </Link>
      </div>

      {loading && (
        <div className="py-24 text-center text-stone-400">載入中...</div>
      )}

      {!loading && notFound && (
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <p className="text-stone-500 mb-4">搵唔到呢則報導，可能已下架。</p>
          <Link to="/media-coverage" className="text-x1493-primary font-bold hover:underline">返返媒體報導頁 →</Link>
        </div>
      )}

      {!loading && item && (
        <article className="max-w-3xl mx-auto px-4 pb-16">
          <div className="flex items-center gap-2 text-xs text-stone-400 mb-3">
            <span className="font-bold text-stone-500">{item.news_date}</span>
            {item.source_name && <span>・{item.source_name}</span>}
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-stone-800 leading-snug mb-6">{item.title}</h1>

          {item.image_url && (
            <img src={item.image_url} alt={item.title} className="w-full rounded-2xl object-cover mb-6" />
          )}

          {body ? (
            <div className="text-stone-700 leading-relaxed whitespace-pre-line text-[15px] md:text-base">{body}</div>
          ) : (
            <p className="text-stone-400">詳細內容整理中，請稍後再睇。</p>
          )}

          {(item.source_name || item.source_url) && (
            <div className="mt-8 rounded-2xl bg-stone-50 border border-stone-100 p-4 flex flex-wrap items-center gap-3">
              <span className="text-sm text-stone-500">資料來源：</span>
              <span className="text-sm font-bold text-stone-700">{item.source_name || '外部報導'}</span>
              {item.source_url && (
                <a
                  href={item.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-bold text-x1493-primary hover:underline"
                >
                  原文出處 <ExternalLink size={14} />
                </a>
              )}
            </div>
          )}

          {/* 上一則／下一則 */}
          {(prev || next) && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {prev ? (
                <Link
                  to={`/media-coverage/${prev.id}`}
                  className="rounded-2xl border border-stone-200 hover:border-x1493-primary p-4 transition group"
                >
                  <span className="flex items-center gap-1 text-xs text-stone-400 mb-1">
                    <ChevronLeft size={14} /> 上一則
                  </span>
                  <span className="text-sm font-bold text-stone-700 group-hover:text-x1493-primary line-clamp-2">{prev.title}</span>
                </Link>
              ) : <span className="hidden sm:block" />}
              {next && (
                <Link
                  to={`/media-coverage/${next.id}`}
                  className="rounded-2xl border border-stone-200 hover:border-x1493-primary p-4 transition group text-right"
                >
                  <span className="flex items-center justify-end gap-1 text-xs text-stone-400 mb-1">
                    下一則 <ChevronRight size={14} />
                  </span>
                  <span className="text-sm font-bold text-stone-700 group-hover:text-x1493-primary line-clamp-2">{next.title}</span>
                </Link>
              )}
            </div>
          )}
        </article>
      )}

      <Footer />
    </div>
  );
}
